"use client";

import dayjs from "dayjs";
import { ChevronDownIcon } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface DateTimePickerProps {
  label?: string;
  value?: string; // ISO datetime string
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function DateTimePicker({
  label,
  value,
  onChange,
  // biome-ignore lint/correctness/noUnusedVariables: <explanation>
  placeholder = "Select date and time",
  disabled = false,
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false);

  // Parse the ISO string to separate date and time
  const dateTime = value ? dayjs(value) : null;
  const selectedDate = dateTime?.toDate();
  const selectedTime = dateTime?.format("HH:mm");

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) {
      onChange?.("");
      return;
    }

    // Keep existing time if available, otherwise use current time
    const existingTime = selectedTime || dayjs().format("HH:mm");
    const [hours, minutes] = existingTime.split(":");

    const newDateTime = dayjs(date)
      .hour(Number.parseInt(hours))
      .minute(Number.parseInt(minutes))
      .second(0)
      .millisecond(0);

    onChange?.(newDateTime.toISOString());
    setOpen(false);
  };

  const handleTimeChange = (timeString: string) => {
    if (!timeString) return;

    const [hours, minutes] = timeString.split(":");
    const baseDate = selectedDate || new Date();

    const newDateTime = dayjs(baseDate)
      .hour(Number.parseInt(hours))
      .minute(Number.parseInt(minutes))
      .second(0)
      .millisecond(0);

    onChange?.(newDateTime.toISOString());
  };

  return (
    <div className="flex gap-4">
      <div className="flex flex-col gap-3">
        {label && (
          <Label htmlFor="date-picker" className="px-1">
            {label}
          </Label>
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              id="date-picker"
              className="w-40 justify-between font-normal"
              disabled={disabled}
            >
              {selectedDate ? selectedDate.toLocaleDateString() : "Select date"}
              <ChevronDownIcon />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto overflow-hidden p-0" align="start">
            <Calendar
              mode="single"
              selected={selectedDate}
              captionLayout="dropdown"
              onSelect={handleDateSelect}
            />
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex flex-col gap-3">
        <Label htmlFor="time-picker" className="px-1">
          Time
        </Label>
        <Input
          type="time"
          id="time-picker"
          value={selectedTime || ""}
          onChange={(e) => handleTimeChange(e.target.value)}
          disabled={disabled}
          className="bg-background appearance-none w-32 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
        />
      </div>
    </div>
  );
}

// Legacy export for backwards compatibility
export function Calendar24() {
  const [open, setOpen] = React.useState(false);
  const [date, setDate] = React.useState<Date | undefined>(undefined);

  return (
    <div className="flex gap-4">
      <div className="flex flex-col gap-3">
        <Label htmlFor="date-picker" className="px-1">
          Date
        </Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              id="date-picker"
              className="w-32 justify-between font-normal"
            >
              {date ? date.toLocaleDateString() : "Select date"}
              <ChevronDownIcon />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto overflow-hidden p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              captionLayout="dropdown"
              onSelect={(date) => {
                setDate(date);
                setOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
      <div className="flex flex-col gap-3">
        <Label htmlFor="time-picker" className="px-1">
          Time
        </Label>
        <Input
          type="time"
          id="time-picker"
          step="1"
          defaultValue="10:30:00"
          className="bg-background appearance-none [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none"
        />
      </div>
    </div>
  );
}
